import React, { useContext, useEffect, useState } from "react";
import {
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  Text as RNText,
  TextInput,
  TouchableOpacity,
  View,
  Image,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useToast } from "react-native-toast-notifications";
import { useDispatch, useSelector } from "react-redux";
import * as ImagePicker from "expo-image-picker";
import { useAppTheme } from "../../../hooks/useAppTheme";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import {
  SampleRequestDetail,
  SampleRequestStatus,
  UserRole,
  Attachment,
  ActivityLogEntry,
} from "../../../models/sampleRequestModel";
import {
  getSampleRequestById,
  approveSampleRequest,
  rejectSampleRequest,
  requestSampleRequestChanges,
  submitSampleRequestChanges,
  submitSampleRequestToFactory,
  approveSampleRequestCAD,
  submitSampleRequestProduction,
  confirmSampleRequestReceived,
  updateSampleRequest,
} from "../../../redux/dispatcher/sampleRequestDispatcher";
import { EditSampleRequestModal } from "./EditSampleRequestModal";
import { getFileFromS3, pushFileToS3 } from "../../../services/s3BucketService";

export type ActivityRole =
  | "Sales"
  | "Management"
  | "China"
  | "Production"
  | "System";

export interface ChangeRequest {
  requestedBy: string;
  requestedFor: "Sales" | "China";
  remarks: string;
  requestedAt: string;
  resolvedAt?: string | null;
}

export interface SampleInfo {
  customer: string;
  dateOfRequest: string;
  itemName: string;
  versionNumber?: string;
  sampleQtyNeeded: number;
  descriptionOfSample?: string;
  purpose: string;
  attachments?: Attachment[];
}

export interface DeliveryInfo {
  deliveryContactPerson: string;
  deliveryContactNumber: string;
  deliveryAddress: string;
  remarks?: string;
}

export interface MouldPreparation {
  drawingProvidedByFactory?: boolean | null;
  toolingRequestBy?: string;
  toolingCost?: string;
  toolingApprovedBy?: string;
  attachments?: Attachment[];
}

export interface TrackingInfo {
  courier?: string;
  trackingNumber?: string;
  shippedDate?: string;
  attachments?: Attachment[];
}

// A locally-picked file, not yet uploaded to S3.
interface PickedFile {
  id: string;
  fileName: string;
  fileUri: string;
  fileType: string;
}

// S3 folder prefix used when uploading NEW mouldPreparation/trackingInfo
// attachments (still needed here since pushFileToS3 requires a folder at
// upload time) — mirrors the delivery module's `pod/${docNum}` convention.
// Display no longer needs this: see renderAttachments, which now reads
// attachment.fileUrl directly since the backend returns it already
// resolved alongside fileName.
const sampleRequestS3Folder = (requestId: string) =>
  `sample-requests/${requestId}`;

// TODO: swap for the actual authenticated user + role once auth/RBAC is
// wired up. Mirrors the "Tim" placeholder used on the list screen.
const CURRENT_USER = { name: "Tim", role: "Sales" as UserRole };

// Kept in sync with the badge colors on SalesSampleRequestScreen.
const statusColorMap: Record<
  string,
  { bg: string; text: string; dot: string }
> = {
  "Pending Approval": { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B" },
  "Changes Requested": { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B" },
  "Sent to Factory": { bg: "#DBEAFE", text: "#1E40AF", dot: "#3B82F6" },
  "Reviewing CAD": { bg: "#DBEAFE", text: "#1E40AF", dot: "#3B82F6" },
  "CAD Changes Requested": { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B" },
  "In Production": { bg: "#DBEAFE", text: "#1E40AF", dot: "#3B82F6" },
  Shipped: { bg: "#E1F0FA", text: "#1D6FA5", dot: "#2F95DC" },
  Completed: { bg: "#D1FAE5", text: "#065F46", dot: "#10B981" },
  Declined: { bg: "#FEE2E2", text: "#991B1B", dot: "#EF4444" },
};

// Purely cosmetic — the header pill reads "Closed" for a completed
// request while the underlying `status` value stays "Completed".
const STATUS_LABEL_OVERRIDES: Partial<Record<SampleRequestStatus, string>> = {
  Completed: "Closed",
};

const ALERT_MESSAGES: Partial<Record<SampleRequestStatus, string>> = {
  "Pending Approval":
    "Awaiting management to review and approve the sample request",
  "Changes Requested": "Awaiting sales to make requested changes",
  "Sent to Factory":
    "Awaiting China to updates the sample CAD Drawing and Tooling Cost",
  "Reviewing CAD": "Awaiting Sales to Approval CAD drawing samples",
  "CAD Changes Requested": "Awaiting China to make requested CAD changes",
  "In Production": "Awaiting China to start production on the sample",
  Shipped: "Awaiting Sales to confirm samples has been received",
};

// ── Date helpers ─────────────────────────────────────────────────────
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatDateLong(input?: string | Date | null): string {
  if (!input) return "-";
  const d = typeof input === "string" ? new Date(input) : input;
  if (isNaN(d.getTime())) return typeof input === "string" ? input : "-";
  const day = String(d.getDate()).padStart(2, "0");
  return `${day} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function formatDateSlash(input?: string | Date | null): string {
  if (!input) return "-";
  const d = typeof input === "string" ? new Date(input) : input;
  if (isNaN(d.getTime())) return typeof input === "string" ? input : "-";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
}

const dash = (v?: string | number | null) =>
  v === undefined || v === null || v === "" ? "-" : String(v);

interface SampleRequestDetailsScreenProps {
  navigation: any;
  route: { params: { request: SampleRequestDetail; requestId?: string } };
  /** Role of the logged-in user viewing this screen — gates which action
   * buttons/forms render inside the Alert card (see mockups: the same
   * "Pending Approval" state renders read-only for Sales and with
   * Approve/Reject/Request Changes for Management). */
  currentUserRole?: UserRole;
  onApprove?: (request: SampleRequestDetail) => void;
  onReject?: (request: SampleRequestDetail, reason: string) => void;
  onRequestChanges?: (
    request: SampleRequestDetail,
    remarks: string,
    requestedFor: "Sales" | "China",
  ) => void;
  onSubmitChanges?: (request: SampleRequestDetail) => void;
  onSubmitToFactory?: (
    request: SampleRequestDetail,
    toolingCost: string,
  ) => void;
  onApproveCAD?: (request: SampleRequestDetail) => void;
  onSubmitProduction?: (
    request: SampleRequestDetail,
    tracking: { courier: string; trackingNumber: string; shippedDate: string },
  ) => void;
  onConfirmReceived?: (request: SampleRequestDetail) => void;
  onCancelRequest?: (request: SampleRequestDetail) => void;
  onEditRequest?: (request: SampleRequestDetail) => void;
}

export default function SampleRequestDetailsScreen({
  navigation,
  route,
  currentUserRole = CURRENT_USER.role,
  onApprove,
  onReject,
  onRequestChanges,
  onSubmitChanges,
  onSubmitToFactory,
  onApproveCAD,
  onSubmitProduction,
  onConfirmReceived,
  onCancelRequest,
  onEditRequest,
}: SampleRequestDetailsScreenProps) {
  const { colors, isDark } = useAppTheme();
  const styles = createStyles(colors, isDark);
  const toast = useToast();
  const { isSmallDevice } = useContext(DeviceOrientationContext);
  const dispatch = useDispatch<any>();

  // The row passed via navigation (from the list screen, already a full
  // document) is shown immediately; `getSampleRequestById` then refreshes
  // it from the server. Once `selectedFromStore` matches this request's
  // id, it takes over as the source of truth — every render helper below
  // (renderSummaryDeliveryCard, renderAlertCard, etc.) keeps reading from
  // `current` exactly as before, so none of that logic needed to change.
  const requestFromRoute = route.params.request;
  const requestId = route.params.requestId || (requestFromRoute as any)?._id;

  const selectedFromStore = useSelector(
    (state: any) => state.sampleRequest.selected,
  );

  const current: SampleRequestDetail =
    selectedFromStore && selectedFromStore._id === requestId
      ? selectedFromStore
      : requestFromRoute;

  useEffect(() => {
    if (requestId) {
      dispatch(getSampleRequestById(requestId));
    }
  }, [requestId]);

  const [promptAction, setPromptAction] = useState<
    null | "reject" | "requestChangesSales" | "requestChangesChina"
  >(null);

  const [toolingCostInput, setToolingCostInput] = useState("");
  const [courierInput, setCourierInput] = useState("");
  const [trackingNumberInput, setTrackingNumberInput] = useState("");
  const [shippedDateInput, setShippedDateInput] = useState("");

  // Pre-fill Tooling Cost from whatever's already on file whenever the
  // request is in a state where submitToFactory can be called — "Sent to
  // Factory" (first submission) or "CAD Changes Requested" (resubmission
  // after Sales asks for CAD changes). Both statuses hit the same backend
  // endpoint (see submitToFactorySchema / assertStatus in the controller),
  // which requires toolingCost either way. Without this, "CAD Changes
  // Requested" had nowhere on screen to enter a cost, so toolingCostInput
  // stayed empty and the "Enter a tooling cost before submitting" guard
  // blocked every resubmission. China can still edit the value here if
  // the cost changed.
  useEffect(() => {
    if (
      current?.status === "Sent to Factory" ||
      current?.status === "CAD Changes Requested"
    ) {
      setToolingCostInput(current?.mouldPreparation?.toolingCost || "");
    }
  }, [current?.status, current?.mouldPreparation?.toolingCost]);

  // Edit modal — the pencil icon in the header opens this instead of
  // firing onEditRequest directly, so Sales/Management can actually
  // change Sample Info / Delivery Info values (matches PATCH /:id, which
  // intentionally never touches status).
  const [editModalVisible, setEditModalVisible] = useState(false);

  // Dev-only role switcher — CURRENT_USER.role is hardcoded until real
  // auth/RBAC ships, so there's currently no way to reach the
  // Management/China/Production action buttons in this screen. This
  // override only takes effect when __DEV__ is true (see DevRoleSwitcher
  // below); strip this block once role-based auth is wired up and
  // currentUserRole comes from the logged-in user.
  const [devRoleOverride, setDevRoleOverride] = useState<UserRole | null>(null);
  const effectiveRole =
    __DEV__ && devRoleOverride ? devRoleOverride : currentUserRole;

  const canAct = (role: UserRole) => effectiveRole === role;

  // ── Attachment upload state ──────────────────────────────────────
  // Locally-picked files staged before "Submit" is pressed (mirrors
  // UploadAttachmentDeliveryContent's `files` staging pattern) — kept
  // separate for the two upload boxes since they attach to different
  // fields (mouldPreparation.attachments vs trackingInfo.attachments).
  const [toolingFiles, setToolingFiles] = useState<PickedFile[]>([]);
  const [productionFiles, setProductionFiles] = useState<PickedFile[]>([]);
  const [isUploadingTooling, setIsUploadingTooling] = useState(false);
  const [isUploadingProduction, setIsUploadingProduction] = useState(false);

  // ── File pickers ─────────────────────────────────────────────────
  // Mirrors UploadAttachmentDeliveryContent's web/native split: a hidden
  // <input type="file"> on web (read via FileReader into a data URI),
  // expo-image-picker's gallery launcher on native.

  const pickFileWeb = (onPicked: (file: PickedFile) => void) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,application/pdf";
    input.style.position = "fixed";
    input.style.left = "-9999px";
    input.style.top = "0";

    const cleanup = () => {
      if (input.parentNode) input.parentNode.removeChild(input);
    };

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) {
        cleanup();
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        onPicked({
          id: Math.random().toString(36).substring(2, 15),
          fileName: file.name || `upload_${Date.now()}`,
          fileUri: reader.result as string,
          fileType: file.type || "application/octet-stream",
        });
        cleanup();
      };
      reader.onerror = () => {
        toast.show("Could not read selected file", { type: "danger" });
        cleanup();
      };
      reader.readAsDataURL(file);
    };

    document.body.appendChild(input);
    input.click();
  };

  const pickFileNative = async (onPicked: (file: PickedFile) => void) => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      quality: 0.5,
      base64: false,
    });
    if (!result.canceled) {
      const asset = result.assets[0];
      onPicked({
        id: Math.random().toString(36).substring(2, 15),
        fileName:
          asset.fileName ||
          `upload_${Date.now()}.${asset.uri.split(".").pop()}`,
        fileUri: asset.uri,
        fileType: asset.mimeType || "image/jpeg",
      });
    }
  };

  const pickFile = (onPicked: (file: PickedFile) => void) => {
    if (Platform.OS === "web") {
      pickFileWeb(onPicked);
    } else {
      pickFileNative(onPicked);
    }
  };

  // Uploads every staged file to S3 (skipping this entirely if the list
  // is empty) and returns Attachment objects matching attachmentSchema
  // ({ fileName, fileUrl, uploadedBy }) ready to send to the backend.
  const uploadFilesToS3 = async (
    files: PickedFile[],
    folder: string,
  ): Promise<Attachment[]> => {
    const uploaded: Attachment[] = [];
    for (const f of files) {
      const uniqueFileName = `${Date.now()}_${f.fileName.replace(/[\s,]+/g, "_")}`;
      const ok = await pushFileToS3(
        "POST",
        f.fileUri,
        uniqueFileName,
        f.fileType,
        folder,
      );
      if (!ok) {
        throw new Error(`Failed to upload ${f.fileName}`);
      }
      const fileData = await getFileFromS3(uniqueFileName, folder);
      uploaded.push({
        fileName: uniqueFileName,
        fileUrl: fileData?.imageUrl,
        uploadedBy: CURRENT_USER.name,
      });
    }
    return uploaded;
  };

  // ── Action handlers ──────────────────────────────────────────────
  // Each dispatches the matching workflow-transition thunk and lets the
  // slice patch `selected`/`list` from the server's response (see
  // sampleRequestReducer's mutationThunks matcher) — no local optimistic
  // state juggling needed since the round trip is fast and the backend
  // is the single source of truth for status + activities.

  const handleApprove = () => {
    dispatch(
      approveSampleRequest({ id: requestId, actorName: CURRENT_USER.name }),
    )
      .unwrap()
      .then((updated: any) => {
        onApprove?.(updated);
        toast.show("Sample request approved", { type: "success" });
      })
      .catch((err: any) => {
        toast.show(err?.message || "Failed to approve the request", {
          type: "danger",
        });
      });
  };

  const handleReject = (reason: string) => {
    dispatch(
      rejectSampleRequest({
        id: requestId,
        actorName: CURRENT_USER.name,
        reason,
      }),
    )
      .unwrap()
      .then((updated: any) => {
        setPromptAction(null);
        onReject?.(updated, reason);
        toast.show("Sample request declined", { type: "warning" });
      })
      .catch((err: any) => {
        toast.show(err?.message || "Failed to decline the request", {
          type: "danger",
        });
      });
  };

  const handleRequestChanges = (
    remarks: string,
    requestedFor: "Sales" | "China",
  ) => {
    dispatch(
      requestSampleRequestChanges({
        id: requestId,
        actorName: CURRENT_USER.name,
        remarks,
        requestedFor,
      }),
    )
      .unwrap()
      .then((updated: any) => {
        setPromptAction(null);
        onRequestChanges?.(updated, remarks, requestedFor);
        toast.show("Changes requested", { type: "success" });
      })
      .catch((err: any) => {
        toast.show(err?.message || "Failed to request changes", {
          type: "danger",
        });
      });
  };

  const handleSubmitChanges = () => {
    dispatch(
      submitSampleRequestChanges({
        id: requestId,
        actorName: CURRENT_USER.name,
      }),
    )
      .unwrap()
      .then((updated: any) => {
        onSubmitChanges?.(updated);
        toast.show("Changes submitted", { type: "success" });
      })
      .catch((err: any) => {
        toast.show(err?.message || "Failed to submit changes", {
          type: "danger",
        });
      });
  };

  const handleSubmitToFactory = async () => {
    if (!toolingCostInput.trim()) {
      toast.show("Enter a tooling cost before submitting", { type: "warning" });
      return;
    }

    try {
      setIsUploadingTooling(true);
      const attachments = await uploadFilesToS3(
        toolingFiles,
        sampleRequestS3Folder(requestId),
      );

      dispatch(
        submitSampleRequestToFactory({
          id: requestId,
          actorName: CURRENT_USER.name,
          toolingCost: toolingCostInput.trim(),
          attachments,
        }),
      )
        .unwrap()
        .then((updated: any) => {
          const submittedCost = toolingCostInput.trim();
          setToolingCostInput("");
          setToolingFiles([]);
          onSubmitToFactory?.(updated, submittedCost);
          toast.show("Submitted to Sales for CAD approval", {
            type: "success",
          });
        })
        .catch((err: any) => {
          toast.show(err?.message || "Failed to submit to the factory", {
            type: "danger",
          });
        });
    } catch (err: any) {
      toast.show(err?.message || "Failed to upload attachment", {
        type: "danger",
      });
    } finally {
      setIsUploadingTooling(false);
    }
  };

  const handleApproveCAD = () => {
    dispatch(
      approveSampleRequestCAD({ id: requestId, actorName: CURRENT_USER.name }),
    )
      .unwrap()
      .then((updated: any) => {
        onApproveCAD?.(updated);
        toast.show("CAD drawing approved", { type: "success" });
      })
      .catch((err: any) => {
        toast.show(err?.message || "Failed to approve the CAD drawing", {
          type: "danger",
        });
      });
  };

  const handleSubmitProduction = async () => {
    if (
      !courierInput.trim() ||
      !trackingNumberInput.trim() ||
      !shippedDateInput.trim()
    ) {
      toast.show("Fill in courier, tracking number and shipped date", {
        type: "warning",
      });
      return;
    }
    const tracking = {
      courier: courierInput.trim(),
      trackingNumber: trackingNumberInput.trim(),
      shippedDate: shippedDateInput.trim(),
    };

    try {
      setIsUploadingProduction(true);
      const attachments = await uploadFilesToS3(
        productionFiles,
        sampleRequestS3Folder(requestId),
      );

      dispatch(
        submitSampleRequestProduction({
          id: requestId,
          actorName: CURRENT_USER.name,
          ...tracking,
          attachments,
        }),
      )
        .unwrap()
        .then((updated: any) => {
          setProductionFiles([]);
          onSubmitProduction?.(updated, tracking);
          toast.show("Marked as shipped", { type: "success" });
        })
        .catch((err: any) => {
          toast.show(err?.message || "Failed to mark as shipped", {
            type: "danger",
          });
        });
    } catch (err: any) {
      toast.show(err?.message || "Failed to upload attachment", {
        type: "danger",
      });
    } finally {
      setIsUploadingProduction(false);
    }
  };

  const handleConfirmReceived = () => {
    dispatch(
      confirmSampleRequestReceived({
        id: requestId,
        actorName: CURRENT_USER.name,
      }),
    )
      .unwrap()
      .then((updated: any) => {
        onConfirmReceived?.(updated);
        toast.show("Sample request completed", { type: "success" });
      })
      .catch((err: any) => {
        toast.show(err?.message || "Failed to confirm receipt", {
          type: "danger",
        });
      });
  };

  // Edit (Sample Info / Delivery Info only) — calls PATCH /sample-requests/:id
  // via the updateSampleRequest thunk. Status is never touched here.
  const handleEditRequest = (data: {
    sampleInfo: Record<string, any>;
    deliveryInfo: Record<string, any>;
  }) => {
    if (current.status === "Declined") {
      toast.show("This request was declined and can no longer be edited", {
        type: "warning",
      });
      setEditModalVisible(false);
      return;
    }

    dispatch(
      updateSampleRequest({
        id: requestId,
        actorName: CURRENT_USER.name,
        sampleInfo: data.sampleInfo,
        deliveryInfo: data.deliveryInfo,
      }),
    )
      .unwrap()
      .then((updated: any) => {
        setEditModalVisible(false);
        onEditRequest?.(updated);
        toast.show("Sample request updated", { type: "success" });
      })
      .catch((err: any) => {
        toast.show(err?.message || "Failed to save changes", {
          type: "danger",
        });
      });
  };

  // ── Small building blocks ────────────────────────────────────────

  const ActionButton = ({
    label,
    tone,
    onPress,
  }: {
    label: string;
    tone: "approve" | "reject" | "neutral";
    onPress: () => void;
  }) => (
    <TouchableOpacity
      style={[
        styles.actionButtonBase,
        tone === "approve" && styles.actionButtonApprove,
        tone === "reject" && styles.actionButtonReject,
        tone === "neutral" && styles.actionButtonNeutral,
      ]}
      onPress={onPress}
    >
      <RNText style={styles.actionButtonText}>{label}</RNText>
    </TouchableOpacity>
  );

  const RemarksPrompt = ({
    placeholder,
    confirmLabel,
    onConfirm,
    onCancel,
  }: {
    placeholder: string;
    confirmLabel: string;
    onConfirm: (text: string) => void;
    onCancel: () => void;
  }) => {
    const [text, setText] = useState("");
    return (
      <View style={styles.remarksPromptBox}>
        <TextInput
          style={styles.remarksPromptInput}
          placeholder={placeholder}
          placeholderTextColor={colors.textSecondary}
          value={text}
          onChangeText={setText}
          multiline
          numberOfLines={2}
        />
        <View style={styles.remarksPromptActions}>
          <TouchableOpacity style={styles.remarksCancelBtn} onPress={onCancel}>
            <RNText style={styles.remarksCancelText}>Cancel</RNText>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.remarksConfirmBtn}
            onPress={() => text.trim() && onConfirm(text.trim())}
          >
            <RNText style={styles.remarksConfirmText}>{confirmLabel}</RNText>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const FormField = ({
    label,
    value,
    onChangeText,
    placeholder,
  }: {
    label: string;
    value: string;
    onChangeText: (t: string) => void;
    placeholder?: string;
  }) => (
    <View style={styles.formFieldWrapper}>
      <RNText style={styles.formFieldLabel}>{label}</RNText>
      <TextInput
        style={styles.formFieldInput}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={colors.textSecondary}
      />
    </View>
  );

  // Staged-but-not-yet-uploaded files, shown under an upload box with a
  // Remove action — mirrors UploadAttachmentDeliveryContent's file list.
  const renderPickedFiles = (
    files: PickedFile[],
    onRemove: (id: string) => void,
  ) => {
    if (files.length === 0) return null;
    return (
      <View style={{ gap: 6, marginBottom: 10 }}>
        {files.map((f) => (
          <View key={f.id} style={styles.mutedRow}>
            <RNText
              style={styles.mutedRowText}
              numberOfLines={1}
              ellipsizeMode="middle"
            >
              {f.fileName}
            </RNText>
            <TouchableOpacity onPress={() => onRemove(f.id)}>
              <RNText style={styles.mutedRowLink}>Remove</RNText>
            </TouchableOpacity>
          </View>
        ))}
      </View>
    );
  };

  // Same extension-sniffing approach as UploadAttachmentDeliveryContent's
  // isImage — fileName always carries the real extension since it's the
  // unique upload key, so this is reliable without needing a stored
  // fileType.
  const isImageFile = (fileName: string) => {
    if (!fileName) return false;
    const lower = fileName.toLowerCase();
    return [".jpg", ".jpeg", ".png", ".gif", ".webp"].some((ext) =>
      lower.endsWith(ext),
    );
  };

  // Already-saved attachments — the backend now returns fileUrl already
  // resolved alongside fileName, so this reads it straight off the
  // document instead of round-tripping through getFileFromS3 on every
  // render (which also depended on knowing the right S3 folder per field
  // — a source of earlier bugs). Image files get an actual thumbnail.
  const renderAttachments = (attachments?: Attachment[]) => {
    if (!attachments || attachments.length === 0) {
      return <RNText style={styles.fieldValue}>-</RNText>;
    }
    return (
      <View style={{ gap: 8 }}>
        {attachments.map((a, idx) => {
          const showThumbnail = isImageFile(a.fileName) && !!a.fileUrl;

          return (
            <TouchableOpacity
              key={idx}
              style={styles.attachmentRow}
              disabled={!a.fileUrl}
              onPress={() => {
                if (a.fileUrl) Linking.openURL(a.fileUrl);
              }}
            >
              {showThumbnail ? (
                <Image
                  source={{ uri: a.fileUrl }}
                  style={styles.attachmentThumbnail}
                  resizeMode="cover"
                />
              ) : (
                <View style={styles.attachmentIconBox}>
                  <Ionicons
                    name="document-text-outline"
                    size={18}
                    color={colors.textSecondary}
                  />
                </View>
              )}
              <RNText
                style={styles.attachmentName}
                numberOfLines={1}
                ellipsizeMode="middle"
              >
                {a.fileName}
              </RNText>
              <RNText style={styles.attachmentView}>View</RNText>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  };

  type FieldSpec = {
    label: string;
    value: React.ReactNode;
    fullWidth?: boolean;
  };

  const renderFieldGrid = (fields: FieldSpec[]) => {
    const rows: FieldSpec[][] = [];
    let i = 0;
    while (i < fields.length) {
      if (fields[i].fullWidth) {
        rows.push([fields[i]]);
        i += 1;
        continue;
      }
      const pair = [fields[i]];
      if (fields[i + 1] && !fields[i + 1].fullWidth) {
        pair.push(fields[i + 1]);
        i += 2;
      } else {
        i += 1;
      }
      rows.push(pair);
    }
    return rows.map((row, idx) => (
      <View key={idx} style={styles.fieldRow}>
        {row.map((f, j) => (
          <View
            key={j}
            style={[styles.fieldCol, row.length === 1 && { flex: 1 }]}
          >
            <RNText style={styles.fieldLabel}>{f.label}</RNText>
            {typeof f.value === "string" || typeof f.value === "number" ? (
              <RNText style={styles.fieldValue}>{f.value}</RNText>
            ) : (
              f.value
            )}
          </View>
        ))}
      </View>
    ));
  };

  // Dev-only role switcher UI. Lets you tap through Sales → Management →
  // China → Production while testing, instead of editing CURRENT_USER and
  // reloading between every step of the workflow.
  const DevRoleSwitcher = () => {
    if (!__DEV__) return null;
    const roles: UserRole[] = ["Sales", "Management", "China", "Production"];
    return (
      <View style={styles.devRoleSwitcher}>
        <RNText style={styles.devRoleSwitcherLabel}>Testing as:</RNText>
        {roles.map((role) => (
          <TouchableOpacity
            key={role}
            style={[
              styles.devRolePill,
              effectiveRole === role && styles.devRolePillActive,
            ]}
            onPress={() => setDevRoleOverride(role)}
          >
            <RNText
              style={[
                styles.devRolePillText,
                effectiveRole === role && styles.devRolePillTextActive,
              ]}
            >
              {role}
            </RNText>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  // ── Alert card ───────────────────────────────────────────────────
  // NOTE: this switch already covers every state shown in your screenshots
  // — Pending Approval (image 1), Changes Requested (image 2), Sent to
  // Factory (image 3), Reviewing CAD (image 4), In Production (image 5),
  // Shipped (image 6), and Completed/"Closed" with the alert hidden and
  // the full activity trail (image 7). Upload boxes for "Sent to Factory"
  // and "In Production" now actually pick + upload files to S3 (see
  // pickFile / uploadFilesToS3 above) instead of being inert.

  const renderAlertCard = () => {
    if (current.status === "Completed") return null;

    if (current.status === "Declined") {
      return (
        <View style={styles.alertCard}>
          <View style={styles.alertTopRow}>
            <View style={styles.alertTitleGroup}>
              <Ionicons name="close-circle-outline" size={18} color="#991B1B" />
              <RNText style={[styles.alertTitle, { color: "#991B1B" }]}>
                Declined
              </RNText>
            </View>
          </View>
          <View style={styles.alertBody}>
            <View style={[styles.alertBar, { backgroundColor: "#EF4444" }]} />
            <RNText style={styles.alertMessage}>
              {current.declinedReason ||
                "This sample request was declined by Management."}
            </RNText>
          </View>
        </View>
      );
    }

    const message = ALERT_MESSAGES[current.status];
    if (!message) return null;

    let inlineActions: React.ReactNode = null;
    let extraContent: React.ReactNode = null;

    switch (current.status) {
      case "Pending Approval": {
        if (canAct("Management")) {
          inlineActions = (
            <View style={styles.alertActionsRow}>
              <ActionButton
                label="Approve"
                tone="approve"
                onPress={handleApprove}
              />
              <ActionButton
                label="Reject"
                tone="reject"
                onPress={() => setPromptAction("reject")}
              />
              <ActionButton
                label="Request Changes"
                tone="neutral"
                onPress={() => setPromptAction("requestChangesSales")}
              />
            </View>
          );
        }
        if (promptAction === "reject") {
          extraContent = (
            <RemarksPrompt
              placeholder="Reason for declining this request"
              confirmLabel="Reject"
              onConfirm={handleReject}
              onCancel={() => setPromptAction(null)}
            />
          );
        } else if (promptAction === "requestChangesSales") {
          extraContent = (
            <RemarksPrompt
              placeholder="What needs to change?"
              confirmLabel="Send to Sales"
              onConfirm={(text) => handleRequestChanges(text, "Sales")}
              onCancel={() => setPromptAction(null)}
            />
          );
        }
        break;
      }

      case "Changes Requested": {
        extraContent = (
          <>
            {current.activeChangeRequest && (
              <View style={styles.remarksBubble}>
                <RNText style={styles.remarksBubbleText}>
                  Remarks: {current.activeChangeRequest.remarks}
                </RNText>
              </View>
            )}
            {canAct("Sales") && (
              <TouchableOpacity
                style={styles.submitButton}
                onPress={handleSubmitChanges}
              >
                <RNText style={styles.submitButtonText}>Submit Changes</RNText>
              </TouchableOpacity>
            )}
          </>
        );
        break;
      }

      case "Sent to Factory": {
        if (canAct("China")) {
          extraContent = (
            <>
              <FormField
                label="Tooling Cost"
                value={toolingCostInput}
                onChangeText={setToolingCostInput}
                placeholder="e.g. RMB 40,000"
              />
              <View style={styles.mutedRow}>
                <RNText style={styles.mutedRowText}>New Update Sample</RNText>
                <RNText style={styles.mutedRowLink}>View</RNText>
              </View>
              {renderPickedFiles(toolingFiles, (id) =>
                setToolingFiles((prev) => prev.filter((f) => f.id !== id)),
              )}
              <TouchableOpacity
                style={styles.uploadBox}
                disabled={isUploadingTooling}
                onPress={() =>
                  pickFile((f) => setToolingFiles((prev) => [...prev, f]))
                }
              >
                <RNText style={styles.uploadBoxText}>
                  {isUploadingTooling
                    ? "Uploading…"
                    : "Upload a drawing or spec sheet"}
                </RNText>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.submitButton}
                onPress={handleSubmitToFactory}
                disabled={isUploadingTooling}
              >
                <RNText style={styles.submitButtonText}>
                  {isUploadingTooling ? "Submitting…" : "Submit"}
                </RNText>
              </TouchableOpacity>
            </>
          );
        }
        break;
      }

      case "Reviewing CAD": {
        if (canAct("Sales")) {
          inlineActions = (
            <View style={styles.alertActionsRow}>
              <ActionButton
                label="Approve"
                tone="approve"
                onPress={handleApproveCAD}
              />
              <ActionButton
                label="Request Changes"
                tone="neutral"
                onPress={() => setPromptAction("requestChangesChina")}
              />
            </View>
          );
        }
        if (promptAction === "requestChangesChina") {
          extraContent = (
            <RemarksPrompt
              placeholder="What needs to change on the CAD drawing?"
              confirmLabel="Send to China"
              onConfirm={(text) => handleRequestChanges(text, "China")}
              onCancel={() => setPromptAction(null)}
            />
          );
        }
        break;
      }

      case "CAD Changes Requested": {
        extraContent = (
          <>
            {current.activeChangeRequest && (
              <View style={styles.remarksBubble}>
                <RNText style={styles.remarksBubbleText}>
                  Remarks: {current.activeChangeRequest.remarks}
                </RNText>
              </View>
            )}
            {canAct("China") && (
              <>
                <FormField
                  label="Tooling Cost"
                  value={toolingCostInput}
                  onChangeText={setToolingCostInput}
                  placeholder="e.g. RMB 40,000"
                />
                {renderPickedFiles(toolingFiles, (id) =>
                  setToolingFiles((prev) => prev.filter((f) => f.id !== id)),
                )}
                <TouchableOpacity
                  style={styles.uploadBox}
                  disabled={isUploadingTooling}
                  onPress={() =>
                    pickFile((f) => setToolingFiles((prev) => [...prev, f]))
                  }
                >
                  <RNText style={styles.uploadBoxText}>
                    {isUploadingTooling
                      ? "Uploading…"
                      : "Upload a revised drawing or spec sheet"}
                  </RNText>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSubmitToFactory}
                  disabled={isUploadingTooling}
                >
                  <RNText style={styles.submitButtonText}>
                    {isUploadingTooling ? "Submitting…" : "Submit Changes"}
                  </RNText>
                </TouchableOpacity>
              </>
            )}
          </>
        );
        break;
      }

      case "In Production": {
        if (canAct("China") || canAct("Production")) {
          extraContent = (
            <>
              <FormField
                label="Courier"
                value={courierInput}
                onChangeText={setCourierInput}
              />
              <FormField
                label="Tracking Number"
                value={trackingNumberInput}
                onChangeText={setTrackingNumberInput}
              />
              <FormField
                label="Shipped Date"
                value={shippedDateInput}
                onChangeText={setShippedDateInput}
                placeholder="DD/MM/YYYY"
              />
              {renderPickedFiles(productionFiles, (id) =>
                setProductionFiles((prev) => prev.filter((f) => f.id !== id)),
              )}
              <TouchableOpacity
                style={styles.uploadBox}
                disabled={isUploadingProduction}
                onPress={() =>
                  pickFile((f) => setProductionFiles((prev) => [...prev, f]))
                }
              >
                <RNText style={styles.uploadBoxText}>
                  {isUploadingProduction
                    ? "Uploading…"
                    : "Upload a evidence of shipped"}
                </RNText>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.submitButton}
                onPress={handleSubmitProduction}
                disabled={isUploadingProduction}
              >
                <RNText style={styles.submitButtonText}>
                  {isUploadingProduction ? "Submitting…" : "Submit"}
                </RNText>
              </TouchableOpacity>
            </>
          );
        }
        break;
      }

      case "Shipped": {
        if (canAct("Sales")) {
          inlineActions = (
            <View style={styles.alertActionsRow}>
              <ActionButton
                label="Sample Received"
                tone="approve"
                onPress={handleConfirmReceived}
              />
            </View>
          );
        }
        break;
      }

      default:
        break;
    }

    return (
      <View style={styles.alertCard}>
        <View style={styles.alertTopRow}>
          <View style={styles.alertTitleGroup}>
            <Ionicons
              name="notifications-outline"
              size={18}
              color={colors.text}
            />
            <RNText style={styles.alertTitle}>Alert</RNText>
          </View>
          {inlineActions}
        </View>
        <View style={styles.alertBody}>
          <View style={styles.alertBar} />
          <RNText style={styles.alertMessage}>{message}</RNText>
        </View>
        {extraContent && (
          <View style={styles.alertExtraContent}>{extraContent}</View>
        )}
      </View>
    );
  };

  // ── Info cards ───────────────────────────────────────────────────

  const renderSummaryDeliveryCard = () => (
    <View style={styles.sectionCard}>
      <RNText style={styles.sectionTitle}>Summary Info</RNText>
      {renderFieldGrid([
        { label: "Customer", value: current.sampleInfo.customer },
        { label: "Requested by", value: current.requestedBy },
        {
          label: "Date request",
          value: formatDateLong(current.sampleInfo.dateOfRequest),
        },
        { label: "Version", value: dash(current.sampleInfo.versionNumber) },
        { label: "Item Name", value: current.sampleInfo.itemName },
        {
          label: "Sample Qty Needed",
          value: `${current.sampleInfo.sampleQtyNeeded} pcs`,
        },
        { label: "Purpose", value: current.sampleInfo.purpose },
        {
          label: "Description of Samples",
          value: dash(current.sampleInfo.descriptionOfSample),
        },
        {
          label: "Attachments",
          value: renderAttachments(current.sampleInfo.attachments),
          fullWidth: true,
        },
      ])}

      <View style={styles.sectionDivider} />

      <RNText style={styles.sectionTitle}>Delivery Info</RNText>
      {renderFieldGrid([
        {
          label: "Delivery Contact",
          value: `${current.deliveryInfo.deliveryContactPerson} | ${current.deliveryInfo.deliveryContactNumber}`,
        },
        {
          label: "Delivery Address",
          value: current.deliveryInfo.deliveryAddress,
        },
        {
          label: "Remarks of Samples",
          value: dash(current.deliveryInfo.remarks),
          fullWidth: true,
        },
      ])}
    </View>
  );

  const renderMouldTrackingCard = () => {
    const mould = current.mouldPreparation;
    const tracking = current.trackingInfo;
    return (
      <View style={styles.sectionCard}>
        <RNText style={styles.sectionTitle}>Mould Preparation</RNText>
        {renderFieldGrid([
          {
            label: "Drawing Provided by Factory",
            value:
              mould?.drawingProvidedByFactory === true
                ? "Yes"
                : mould?.drawingProvidedByFactory === false
                  ? "No"
                  : "-",
          },
          { label: "Tooling Request by", value: dash(mould?.toolingRequestBy) },
          { label: "Tooling Cost", value: dash(mould?.toolingCost) },
          {
            label: "Tooling Approved by",
            value: dash(mould?.toolingApprovedBy),
          },
          {
            label: "Attachments",
            value: renderAttachments(mould?.attachments),
            fullWidth: true,
          },
        ])}

        <View style={styles.sectionDivider} />

        <RNText style={styles.sectionTitle}>Tracking Info</RNText>
        {renderFieldGrid([
          { label: "Courier", value: dash(tracking?.courier) },
          { label: "Tracking Number", value: dash(tracking?.trackingNumber) },
          {
            label: "Shipped Date",
            value: formatDateSlash(tracking?.shippedDate),
          },
          {
            label: "Attachments",
            value: renderAttachments(tracking?.attachments),
            fullWidth: true,
          },
        ])}
      </View>
    );
  };

  const renderActivitiesCard = () => (
    <View style={styles.activitiesCard}>
      <RNText style={styles.sectionTitle}>Activites</RNText>
      {current.activities.length === 0 ? (
        <RNText style={styles.fieldValue}>No activity yet</RNText>
      ) : (
        current.activities.map((activity, idx) => (
          <View key={idx} style={styles.activityItem}>
            <View style={styles.activityHeaderRow}>
              <View style={styles.activityRoleBadge}>
                <RNText style={styles.activityRoleBadgeText}>
                  {activity.role}
                </RNText>
              </View>
              <RNText style={styles.activityActorName}>
                {activity.actorName}
              </RNText>
              <View style={{ flex: 1 }} />
              <RNText style={styles.activityDate}>
                {formatDateLong(activity.date)}
              </RNText>
            </View>
            <RNText style={styles.activityDescription}>
              {activity.description}
            </RNText>
          </View>
        ))
      )}
    </View>
  );

  const statusColors = statusColorMap[current.status] || {
    bg: colors.background,
    text: colors.textSecondary,
    dot: colors.textSecondary,
  };

  return (
    <View style={styles.pageWrapper}>
      <View style={styles.headerBar}>
        <TouchableOpacity
          onPress={() => navigation.navigate("SalesSampleRequestScreen")}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={20} color={colors.text} />
        </TouchableOpacity>
        <RNText style={styles.headerRequestId}>{current.requestId}</RNText>
        <View
          style={[
            styles.headerStatusPill,
            { backgroundColor: statusColors.bg },
          ]}
        >
          <RNText
            style={[styles.headerStatusText, { color: statusColors.text }]}
          >
            {STATUS_LABEL_OVERRIDES[current.status] || current.status}
          </RNText>
        </View>
        <View style={{ flex: 1 }} />
        {/* <TouchableOpacity
          style={styles.headerIconButton}
          onPress={() => onCancelRequest?.(current)}
        >
          <Ionicons name="ban-outline" size={18} color={colors.textSecondary} />
        </TouchableOpacity> */}
        {current.status !== "Declined" &&
          current.status === "Pending Approval" && (
            <TouchableOpacity
              style={styles.headerIconButton}
              onPress={() => setEditModalVisible(true)}
            >
              <Ionicons
                name="pencil-outline"
                size={18}
                color={colors.textSecondary}
              />
            </TouchableOpacity>
          )}
      </View>

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.container}>
          <DevRoleSwitcher />
          {renderAlertCard()}

          <View style={[styles.bodyRow, isSmallDevice && styles.bodyColumn]}>
            <View style={styles.leftColumn}>
              {renderSummaryDeliveryCard()}
              {renderMouldTrackingCard()}
            </View>
            <View style={styles.rightColumn}>{renderActivitiesCard()}</View>
          </View>
        </View>
      </ScrollView>

      {editModalVisible && (
        <EditSampleRequestModal
          visible={editModalVisible}
          onClose={() => setEditModalVisible(false)}
          request={current}
          onSubmit={handleEditRequest}
        />
      )}
    </View>
  );
}

function createStyles(colors: any, isDark: boolean) {
  return StyleSheet.create({
    pageWrapper: {
      flex: 1,
      backgroundColor: isDark ? colors.background : "#FFFFFF",
    },

    // ── Header ───────────────────────────────────────
    headerBar: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 16,
      paddingVertical: 14,
      borderBottomWidth: 1,
      borderBottomColor: isDark ? colors.border : "#E2E8F0",
      gap: 12,
    },
    backButton: {
      padding: 4,
    },
    headerRequestId: {
      fontSize: 15,
      fontWeight: "700",
      color: isDark ? colors.text : "#0F172A",
    },
    headerStatusPill: {
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 999,
    },
    headerStatusText: {
      fontSize: 11,
      fontWeight: "700",
    },
    headerIconButton: {
      padding: 6,
      borderRadius: 8,
    },

    scrollView: {
      flex: 1,
    },
    container: {
      padding: 16,
      gap: 16,
    },

    // ── Dev role switcher (testing only) ──────────────
    devRoleSwitcher: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      backgroundColor: isDark ? colors.background : "#FFF7ED",
      borderWidth: 1,
      borderColor: "#FDBA74",
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 8,
      flexWrap: "wrap",
    },
    devRoleSwitcherLabel: {
      fontSize: 12,
      fontWeight: "700",
      color: "#9A3412",
      marginRight: 4,
    },
    devRolePill: {
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 999,
      backgroundColor: "#FFFFFF",
      borderWidth: 1,
      borderColor: "#FDBA74",
    },
    devRolePillActive: {
      backgroundColor: "#EA580C",
      borderColor: "#EA580C",
    },
    devRolePillText: {
      fontSize: 12,
      fontWeight: "600",
      color: "#9A3412",
    },
    devRolePillTextActive: {
      color: "#FFFFFF",
    },

    // ── Alert card ───────────────────────────────────
    alertCard: {
      backgroundColor: isDark ? colors.surface : "#F7FAFC",
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      padding: 18,
      gap: 14,
    },
    alertTopRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 10,
    },
    alertTitleGroup: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    alertTitle: {
      fontSize: 15,
      fontWeight: "700",
      color: isDark ? colors.text : "#0F172A",
    },
    alertActionsRow: {
      flexDirection: "row",
      gap: 8,
      flexWrap: "wrap",
    },
    alertBody: {
      flexDirection: "row",
      gap: 12,
    },
    alertBar: {
      width: 3,
      borderRadius: 2,
      backgroundColor: isDark ? colors.border : "#CBD5E1",
    },
    alertMessage: {
      flex: 1,
      fontSize: 13,
      color: isDark ? colors.text : "#334155",
    },
    alertExtraContent: {
      gap: 4,
    },

    actionButtonBase: {
      paddingHorizontal: 16,
      paddingVertical: 9,
      borderRadius: 8,
    },
    actionButtonApprove: {
      backgroundColor: "#4CAF71",
    },
    actionButtonReject: {
      backgroundColor: "#EF7A6E",
    },
    actionButtonNeutral: {
      backgroundColor: "#D6C452",
    },
    actionButtonText: {
      color: "#FFFFFF",
      fontSize: 13,
      fontWeight: "700",
    },

    submitButton: {
      backgroundColor: isDark ? colors.primary : "#2F95DC",
      borderRadius: 8,
      paddingVertical: 12,
      alignItems: "center",
      marginTop: 4,
    },
    submitButtonText: {
      color: "#FFFFFF",
      fontSize: 14,
      fontWeight: "700",
    },

    formFieldWrapper: {
      marginBottom: 4,
    },
    formFieldLabel: {
      fontSize: 13,
      fontWeight: "500",
      color: isDark ? colors.textSecondary : "#374151",
      marginBottom: 6,
    },
    formFieldInput: {
      backgroundColor: isDark ? colors.background : "#FFFFFF",
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 13,
      color: isDark ? colors.text : "#0F172A",
    },

    mutedRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: isDark ? colors.background : "#EEF2F6",
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
    },
    mutedRowText: {
      fontSize: 13,
      color: isDark ? colors.text : "#334155",
      flex: 1,
      marginRight: 8,
    },
    mutedRowLink: {
      fontSize: 13,
      fontWeight: "600",
      color: isDark ? colors.primary : "#2F95DC",
    },

    uploadBox: {
      backgroundColor: isDark ? colors.background : "#F8FAFC",
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      borderStyle: "dashed",
      paddingVertical: 14,
      alignItems: "center",
    },
    uploadBoxText: {
      fontSize: 12,
      color: colors.textSecondary,
    },

    remarksBubble: {
      backgroundColor: isDark ? colors.background : "#E5E7EB",
      borderRadius: 999,
      paddingHorizontal: 16,
      paddingVertical: 10,
      alignSelf: "flex-start",
    },
    remarksBubbleText: {
      fontSize: 13,
      color: isDark ? colors.text : "#374151",
    },

    remarksPromptBox: {
      gap: 10,
    },
    remarksPromptInput: {
      backgroundColor: isDark ? colors.background : "#FFFFFF",
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 13,
      minHeight: 60,
      textAlignVertical: "top",
      color: isDark ? colors.text : "#0F172A",
    },
    remarksPromptActions: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: 8,
    },
    remarksCancelBtn: {
      paddingHorizontal: 14,
      paddingVertical: 9,
      borderRadius: 8,
      backgroundColor: isDark ? colors.background : "#E2E8F0",
    },
    remarksCancelText: {
      fontSize: 13,
      fontWeight: "600",
      color: isDark ? colors.text : "#334155",
    },
    remarksConfirmBtn: {
      paddingHorizontal: 14,
      paddingVertical: 9,
      borderRadius: 8,
      backgroundColor: isDark ? colors.primary : "#2F95DC",
    },
    remarksConfirmText: {
      fontSize: 13,
      fontWeight: "700",
      color: "#FFFFFF",
    },

    // ── Body layout ──────────────────────────────────
    bodyRow: {
      flexDirection: "row",
      gap: 16,
      alignItems: "flex-start",
    },
    bodyColumn: {
      flexDirection: "column",
    },
    leftColumn: {
      flex: 2,
      gap: 16,
      minWidth: 0,
    },
    rightColumn: {
      flex: 1,
      minWidth: 0,
    },

    // ── Section cards ────────────────────────────────
    sectionCard: {
      backgroundColor: isDark ? colors.surface : "#F7FAFC",
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      padding: 18,
    },
    sectionTitle: {
      fontSize: 15,
      fontWeight: "700",
      color: isDark ? colors.text : "#0F172A",
      marginBottom: 14,
    },
    sectionDivider: {
      height: 1,
      backgroundColor: isDark ? colors.border : "#E2E8F0",
      marginVertical: 16,
    },

    fieldRow: {
      flexDirection: "row",
      gap: 20,
      marginBottom: 16,
    },
    fieldCol: {
      flex: 1,
      minWidth: 0,
    },
    fieldLabel: {
      fontSize: 12,
      fontWeight: "600",
      color: isDark ? colors.textSecondary : "#64748B",
      marginBottom: 4,
    },
    fieldValue: {
      fontSize: 13,
      color: isDark ? colors.text : "#0F172A",
    },

    attachmentRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    attachmentThumbnail: {
      width: 32,
      height: 32,
      borderRadius: 4,
      backgroundColor: isDark ? colors.background : "#F1F5F9",
    },
    attachmentIconBox: {
      width: 32,
      height: 32,
      borderRadius: 4,
      backgroundColor: isDark ? colors.background : "#F1F5F9",
      alignItems: "center",
      justifyContent: "center",
    },
    attachmentName: {
      fontSize: 13,
      color: isDark ? colors.text : "#334155",
      flex: 1,
      flexShrink: 1,
    },
    attachmentView: {
      fontSize: 13,
      fontWeight: "600",
      color: isDark ? colors.primary : "#2F95DC",
    },

    // ── Activities ───────────────────────────────────
    activitiesCard: {
      backgroundColor: isDark ? colors.surface : "#F7FAFC",
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      padding: 18,
    },
    activityItem: {
      borderLeftWidth: 2,
      borderLeftColor: isDark ? colors.border : "#CBD5E1",
      paddingLeft: 12,
      marginBottom: 16,
    },
    activityHeaderRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      marginBottom: 4,
    },
    activityRoleBadge: {
      backgroundColor: "#94A3B8",
      borderRadius: 4,
      paddingHorizontal: 6,
      paddingVertical: 2,
    },
    activityRoleBadgeText: {
      fontSize: 10,
      fontWeight: "700",
      color: "#FFFFFF",
    },
    activityActorName: {
      fontSize: 13,
      fontWeight: "700",
      color: isDark ? colors.text : "#0F172A",
    },
    activityDate: {
      fontSize: 12,
      color: isDark ? colors.textSecondary : "#64748B",
    },
    activityDescription: {
      fontSize: 13,
      color: isDark ? colors.textSecondary : "#475569",
    },
  });
}
